import { useEffect, useState } from 'react';

import { SignalsSpinner } from './SignalsSpinner';

/**
 * The line that stands in for an answer while one is being worked out.
 *
 * It used to be a static sentence the app sent as an ordinary message —
 * "🔍 Analyzing your question…" — which says the same thing at second 2 and at
 * second 40, so a long question looked indistinguishable from a hung one.
 *
 * Here the mark turns and breathes, and the word under it changes on the same
 * 2.6s beat, dimming as the spinner dims and brightening as it brightens. The
 * words are chosen to describe what the backend is actually doing at that
 * phase, so the line carries information rather than just motion.
 *
 * Beside the word, the time the user has been waiting, counted from when the
 * server started the turn (metadata.signals_started_at) so a tab that opens
 * the thread halfway through shows the real wait. The mark moves through its
 * shapes instead of only turning (SignalsSpinner morph).
 *
 * English always: this is the system talking, not the answer.
 */

const PHASE_WORDS: Record<string, string[]> = {
  // Waiting for a sandbox container to be free.
  preparing: [
    'Warming up',
    'Opening your data session',
    'Reserving a workspace',
    'Almost ready'
  ],
  // The model is deciding what to ask the data.
  analyzing: [
    'Signaling',
    'Reading your question',
    'Choosing the tables',
    'Framing the query',
    'Lining up the numbers'
  ],
  // Generated code is running against the tenant's data.
  running: [
    'Crunching',
    'Reading the ledgers',
    'Tallying',
    'Reconciling',
    'Cross-checking',
    'Rounding it off'
  ]
};

const FALLBACK = PHASE_WORDS.analyzing;

/** One full breath of the mark. The word turns over on the same beat. */
const BEAT_MS = 2600;

// A start further off than this is a clock that disagrees with the server's,
// not a real wait: count from when the line appeared instead.
const MAX_BELIEVABLE_MS = 2 * 60 * 60 * 1000;
const CLOCK_SLACK_MS = 5000;

/** "7 s", "48 s", "1 min 05 s" — whole seconds; it ticks once a second. */
export function formatElapsed(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  if (total < 60) return `${total} s`;
  const s = total % 60;
  return `${Math.floor(total / 60)} min ${s < 10 ? '0' : ''}${s} s`;
}

/** The moment to count from: the server's start when it is believable. */
export function waitStart(
  startedAt: unknown,
  shownAt: number,
  now: number
): number {
  if (typeof startedAt !== 'number' || !Number.isFinite(startedAt)) {
    return shownAt;
  }
  if (startedAt > now + CLOCK_SLACK_MS || now - startedAt > MAX_BELIEVABLE_MS) {
    return shownAt;
  }
  return Math.min(startedAt, now);
}

/** Seconds since the wait began, re-rendered once a second. */
export function useElapsedSeconds(startedAt?: number): number {
  const [shownAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(tick);
  }, []);
  return (now - waitStart(startedAt, shownAt, now)) / 1000;
}

interface Props {
  /** Which stage the backend says it is at; unknown phases get the middle one. */
  phase?: string;
  /** Shown under the word — e.g. how many tables are being read. */
  detail?: string;
  /** When the user's wait began (epoch ms, metadata.signals_started_at). */
  startedAt?: number;
}

export const SignalsStatus = ({ phase, detail, startedAt }: Props) => {
  const words = (phase && PHASE_WORDS[phase]) || FALLBACK;
  const [index, setIndex] = useState(0);
  const elapsed = useElapsedSeconds(startedAt);

  useEffect(() => {
    setIndex(0);
    // Swap at the trough of the breath, not at its peak, so the word is at its
    // dimmest as it changes and the swap reads as one word fading into the
    // next rather than a flicker.
    const start = window.setTimeout(() => {
      setIndex((i) => i + 1);
    }, BEAT_MS / 2);
    const tick = window.setInterval(() => {
      setIndex((i) => i + 1);
    }, BEAT_MS);
    return () => {
      window.clearTimeout(start);
      window.clearInterval(tick);
    };
  }, [phase]);

  const word = words[index % words.length];

  return (
    <div
      className="flex items-center gap-3"
      role="status"
      aria-live="polite"
      // The words are a progress indication, not content: announcing every
      // change would talk over a screen-reader user for the whole wait.
      aria-atomic="true"
    >
      <SignalsSpinner size="md" morph />
      <div className="flex flex-col min-w-0">
        <span className="flex items-baseline gap-2">
          <span
            key={word}
            className="sig-status-word text-[14.5px] font-medium"
            style={{ color: 'var(--sig-body)' }}
          >
            {word}
            <span className="sig-status-ellipsis" aria-hidden="true">
              …
            </span>
          </span>
          {/* Not announced: a number changing every second would talk over
              a screen-reader user for the whole wait. */}
          <span
            className="text-[12.5px] tabular-nums"
            style={{ color: 'var(--sig-subtle)' }}
            aria-hidden="true"
            data-testid="signals-status-elapsed"
          >
            {formatElapsed(elapsed)}
          </span>
        </span>
        {detail ? (
          <span
            className="text-[12.5px] mt-0.5"
            style={{ color: 'var(--sig-subtle)' }}
            dir="auto"
          >
            {detail}
          </span>
        ) : null}
      </div>
    </div>
  );
};

/**
 * True when a message is one of the app's transient status lines rather than an
 * answer. The backend marks them in metadata; the literal text is kept as the
 * fallback for an older client, and as what the thread transcript filters on.
 */
export const statusPhaseOf = (
  metadata: Record<string, any> | undefined
): string | undefined => {
  const phase = metadata?.signals_status;
  return typeof phase === 'string' && phase ? phase : undefined;
};
